import { PropertyMetadata } from '../abstractStyles/types';
import { getParentShorthands } from '../grouping/propertyRelationships';

/**
 * Validates whether a preset can be applied to the current component
 * by checking if all preset properties exist in the manifest.
 *
 * Validation rules:
 * 1. If the property exists directly in metadata → Valid
 * 2. If any metadata entry has cssPropertyType matching the property → Valid
 *    (handles custom properties like '--border-theme' with cssPropertyType: 'border')
 * 3. If any metadata entry has cssPropertyType matching a parent shorthand → Valid
 *    (handles decomposable custom properties)
 * 4. Otherwise → Invalid
 *
 * @param presetValues - CSS property values the preset wants to set
 * @param metadata - Property metadata from the manifest
 * @returns true if the preset can be applied, false otherwise
 */
export function validatePreset(
  presetValues: Record<string, any>,
  metadata: Record<string, PropertyMetadata>,
): boolean {
  const availableProperties = new Set(Object.keys(metadata));
  const availableCssPropertyTypes = new Set(
    Object.values(metadata).map((m) => m.cssPropertyType),
  );

  for (const property of Object.keys(presetValues)) {
    // Rule 1: Check if property exists directly as a key
    if (availableProperties.has(property)) {
      continue;
    }

    // Rule 2: Check if any metadata entry has cssPropertyType matching the property
    // This handles custom properties like '--border-theme' with cssPropertyType: 'borderWidth'
    if (availableCssPropertyTypes.has(property)) {
      continue;
    }

    // Rule 3: Check if any metadata entry has cssPropertyType matching a parent shorthand
    // This handles decomposable properties (both regular CSS and custom properties)
    const parentShorthands = getParentShorthands(property);
    const hasParentInCssPropertyTypes = parentShorthands.some((parent) =>
      availableCssPropertyTypes.has(parent),
    );

    if (hasParentInCssPropertyTypes) {
      continue;
    }

    // Property not found in any form
    return false;
  }

  return true;
}

/**
 * Filters presets to only include those that can be applied.
 *
 * @param presets - Array of presets to filter
 * @param metadata - Property metadata from the manifest
 * @returns Filtered array of valid presets
 */
export function filterValidPresets<T extends { values: Record<string, any> }>(
  presets: T[],
  metadata: Record<string, PropertyMetadata>,
): T[] {
  return presets.filter((preset) => validatePreset(preset.values, metadata));
}
